import axios from 'axios';
import { SiteConfig } from './SiteConfig.interface';
import { mapSiteConfig } from './SiteConfigAdapter';

export const getFallbackConfig = (): SiteConfig => {
  return {
    cmsBaseUrl: 'https://bloomreachsite-dev.genetec.com',
    spaBaseUrl: 'https://local.genetec.com:3000',
    primaryFontFamily: 'Circular, helvetica, arial, sans-serif',
    secondaryFontFamily: 'Garamond Premier Pro, Times New Roman, serif',
    adobeFontKitId: 'mocked-adobe-kit',
    googleFontPrimary: 'Roboto:300,400,700',
    googleFontSecondary: 'Open Sans:300,400',
    ssoClientId: '07c357f7b74641d49088009dcf7468c5',
    ssoAuthority: 'https://login.genetec.com',
    ssoBaseUrl: 'https://login.genetec.com',
    ssoRedirectPath: '/callback',
    ssoPostLogoutRedirectPath: '/logout_callback',
    ssoScope: 'openid profile source_iss groups email partners_portal.api',
    ssoRoot: '/portal',
    ssoContactRegistrationPath: '/contact-registration',
    ssoInvitedRegistrationPath: '/invited-registration',
    ssoUnauthorizedPath: '/unauthorized',
    ssoForbiddenPath: '/forbidden',
    ssoPendingApprovalPath: '/pending-approval',
    ssoOldPortalUrl: 'https://portal.genetec.com/',
    ssoBetaUserRedirectionEnabled: true,
    ssoAntiCache: false,
    powerAppsBaseUrl: 'https://paportal-dev.genetec.com/en-US/',
    powerAppsSignInUri: 'https://paportal-dev.genetec.com/en-US/gen_powerappsconnect/',
    powerAppsSignOutUri: 'https://paportal-dev.genetec.com/en-US/Account/Login/LogOff',
    powerAppsMinutesExpiration: '59',
    experlogixSignInUri:
      'https://genetecportaldev.experlogixonline.com/experlogix/configurator/ui/geneteclogin.aspx?action=none&provider=OpenIdConnect',
    experlogixSignOutUri: 'https://genetecportaldev.experlogixonline.com/logout',
    experlogixMinutesExpiration: '17',
    debugModeEnabled: true,
    ssoUiLocale: 'en',
    blockedVideoDomains: [],
    portalSearchRedirect: {
      from: '/search',
      to: '/portal/search',
    },
    portalHomepageRedirects: {},
  };
};

export const applyConfigToWindow = (config: SiteConfig) => {
  if (config.cmsBaseUrl) window.cmsBaseUrl = config.cmsBaseUrl;
  if (config.spaBaseUrl) window.spaBaseUrl = config.spaBaseUrl;
  if (config.portalHomepageRedirects)
    window.portalHomepageRedirects = config.portalHomepageRedirects;
  if (config.portalSearchRedirect) window.portalSearchRedirect = config.portalSearchRedirect;
  if (config.primaryFontFamily) window.primaryFontFamily = config.primaryFontFamily;
  if (config.secondaryFontFamily) window.secondaryFontFamily = config.secondaryFontFamily;
  if (config.adobeFontKitId) window.adobeFontKitId = config.adobeFontKitId;
  if (config.googleFontPrimary) window.googleFontPrimary = config.googleFontPrimary;
  if (config.googleFontSecondary) window.googleFontSecondary = config.googleFontSecondary;
  if (config.ssoClientId) window.ssoClientId = config.ssoClientId;
  if (config.ssoAuthority) window.ssoAuthority = config.ssoAuthority;
  if (config.ssoBaseUrl) window.ssoBaseUrl = config.ssoBaseUrl;
  if (config.ssoRedirectPath) window.ssoRedirectPath = config.ssoRedirectPath;
  if (config.ssoPostLogoutRedirectPath)
    window.ssoPostLogoutRedirectPath = config.ssoPostLogoutRedirectPath;
  if (config.ssoScope) window.ssoScope = config.ssoScope;
  if (config.ssoRoot) window.ssoRoot = config.ssoRoot;
  if (config.ssoContactRegistrationPath)
    window.ssoContactRegistrationPath = config.ssoContactRegistrationPath;
  if (config.ssoInvitedRegistrationPath)
    window.ssoInvitedRegistrationPath = config.ssoInvitedRegistrationPath;
  if (config.ssoUnauthorizedPath) window.ssoUnauthorizedPath = config.ssoUnauthorizedPath;
  if (config.ssoForbiddenPath) window.ssoForbiddenPath = config.ssoForbiddenPath;
  if (config.ssoPendingApprovalPath) window.ssoPendingApprovalPath = config.ssoPendingApprovalPath;
  if (config.ssoOldPortalUrl) window.ssoOldPortalUrl = config.ssoOldPortalUrl;

  if (config.ssoBetaUserRedirectionEnabled !== undefined) {
    window.ssoBetaUserRedirectionEnabled = String(config.ssoBetaUserRedirectionEnabled);
  }
  if (config.ssoAntiCache !== undefined) {
    window.ssoAntiCache = Boolean(config.ssoAntiCache);
  }

  if (config.powerAppsBaseUrl) window.powerAppsBaseUrl = config.powerAppsBaseUrl;
  if (config.powerAppsSignInUri) window.powerAppsSignInUri = config.powerAppsSignInUri;
  if (config.powerAppsSignOutUri) window.powerAppsSignOutUri = config.powerAppsSignOutUri;
  if (config.powerAppsMinutesExpiration)
    window.powerAppsMinutesExpiration = String(config.powerAppsMinutesExpiration);

  if (config.experlogixSignInUri) window.experlogixSignInUri = config.experlogixSignInUri;
  if (config.experlogixSignOutUri) window.experlogixSignOutUri = config.experlogixSignOutUri;
  if (config.experlogixMinutesExpiration)
    window.experlogixMinutesExpiration = String(config.experlogixMinutesExpiration);

  if (config.debugModeEnabled !== undefined)
    window.debugModeEnabled = Boolean(config.debugModeEnabled);
  if (config.ssoUiLocale) window.ssoUiLocale = config.ssoUiLocale;

  window.blockedVideoDomains = config.blockedVideoDomains || [];
};

export const bootstrapSiteConfig = async (): Promise<void> => {
  try {
    document.body.classList.add('loading');
    const apiUrl = process.env.REACT_APP_SITE_CONFIG_API_URL || '/api/site-config';
    const response = await axios.get(apiUrl);
    const mappedConfig = mapSiteConfig(response.data);
    applyConfigToWindow(mappedConfig);
    console.log('--- API CONFIGURATION APPLIED SUCCESSFULLY ---', mappedConfig);
  } catch (err) {
    console.error('Failed to load site config from API, using fallback:', err);
    const fallbackConfig = getFallbackConfig();
    console.log('--- FALLBACK CONFIGURATION ---', fallbackConfig);
    applyConfigToWindow(fallbackConfig);
  } finally {
    document.body.classList.remove('loading');
  }
};
